import { useCallback, useEffect } from 'react';
import { GET_HEALTH_DASHBOARD_URL, HAS_FACE_SCAN_RESULTS } from '../../../state/mutations/User';
import { IntelliProveStage, intelliproveCallbackOnEvent } from '../../../utils/intelliprove/callbackOnIntelliproveEvent';
import { captureException } from '@sentry/react-native';
import { useMutation } from '@apollo/client';
import { useStores } from '../../useStores';
import { useGoToFaceScan } from './useGoToFaceScan';

export interface IntelliProveMessage {
  stage: IntelliProveStage;
  data?: { hasResults?: boolean };
}

export interface UseHealthDashboardResult {
  /** Whether the user has face scan results (null = not yet checked) */
  hasFaceScanResults: boolean | null;
  /** The dashboard URL */
  dashboardUrl: string | undefined;
  /** Whether the dashboard URL is loading */
  loading: boolean;
  /** Error from fetching the dashboard URL */
  error: any;
  /** Labels for UI text */
  labels: {
    settings: any;
    health: any;
  };
  /** Retry fetching the dashboard URL */
  retry: () => void;
  /** Handle an IntelliProve message (from WebView or iframe) */
  handleIntelliProveMessage: (message: IntelliProveMessage) => void;
  /** Navigate to face scan */
  goToFaceScan: () => void;
}

export function useHealthDashboard(): UseHealthDashboardResult {
  const rootStore = useStores();
  const { language } = rootStore;
  const [getHealthDashboardUrl, { data, loading, error }] = useMutation(GET_HEALTH_DASHBOARD_URL, { fetchPolicy: 'no-cache' });
  const [checkFaceScanResults] = useMutation(HAS_FACE_SCAN_RESULTS, { fetchPolicy: 'no-cache' });
  const { goToFaceScan } = useGoToFaceScan();

  const hasFaceScanResults = rootStore?.user?.hasFaceScanResults;

  // Only fetch face scan results if not yet checked (value is null in store)
  useEffect(() => {
    if (hasFaceScanResults === null) {
      checkFaceScanResults({
        onCompleted: (result) => {
          const hasResults = result?.hasFaceScanResults === true;
          rootStore?.user?.setHasFaceScanResults(hasResults);
        },
        onError: err => {
          console.error('Error checking face scan results:', err);
          captureException(err);
          // Set to false on error so we don't keep retrying
          rootStore?.user?.setHasFaceScanResults(false);
        },
      });
    }
  }, [hasFaceScanResults, checkFaceScanResults, rootStore?.user]);

  // Fetch dashboard URL when user has face scan results
  useEffect(() => {
    if (hasFaceScanResults) {
      getHealthDashboardUrl({
        variables: { language: language.language },
        onError: err => {
          console.error(err);
          captureException(err);
        },
      });
    }
  }, [getHealthDashboardUrl, language.language, hasFaceScanResults]);

  const handleIntelliProveMessage = useCallback((message: IntelliProveMessage) => {
    if (message?.stage) {
      intelliproveCallbackOnEvent(message.stage, message.data, rootStore?.user?.setHasFaceScanResults);
    }
  }, [rootStore?.user]);

  const retry = useCallback(() => {
    getHealthDashboardUrl({ variables: { language: language.language } });
  }, [getHealthDashboardUrl, language.language]);

  return {
    hasFaceScanResults,
    dashboardUrl: data?.url,
    loading,
    error,
    labels: {
      settings: rootStore?.language?.translate?.MOBILE?.SETTINGS,
      health: rootStore?.language?.translate?.MOBILE?.HEALTH,
    },
    retry,
    handleIntelliProveMessage,
    goToFaceScan,
  };
}
